import {Controller, FieldArrayWithId, UseFormReturn} from 'react-hook-form'
import {BulkOrderFormValues} from '../../BulkOrdersFormValues'
import {
  ActiveMaterialDeliveryItem,
  OrderIntakeOption,
  OrderIntakeOptions,
  OrderRequest,
  OrdersFormValidityState,
  PersistedOrderRequest
} from '../../declarations/types'
import {InvalidOrderIntakeOptions} from '../../Options/query'
import {useBulkOrderFormChangeHandlerWithMethods} from '../../FormChangeHandlers'
import {
  PersistedMaterialsAction,
  PersistedMaterialsBanner
} from '../../components/PersistedMaterialsDialog/PersistedMaterialsBanner'
import {useMaterialEditorController} from '../hooks/useMaterialEditorController'
import {BusinessLineType} from '../../../common/types'
import {Box, Grid, Hidden, Paper, Stack, Table, TableBody, TableContainer} from '@mui/material'
import {Typography} from '@hconnect/uikit'
import {MaterialSectionHeader, MaterialsHeader} from '../../components/VisualComponents'
import {SiteDropdown} from '../../components/SiteDropdown'
import {SlotOrderWindowAlert} from '../containers/slotManagement/components/SlotOrderWindowAlert'
import {CollectMaterialForm} from './CollectMaterialForm'
import {AddAnotherButton} from '../../components/AddAnotherButton'
import {ContactSection} from '../containers/ContactSection'
import {partialRight} from 'lodash'
import {ShippingType} from '../../declarations/OrderIntake.enums'
import {invalidateHubQuery} from '../../../common/react-query/QueryProvider'
import distributedCacheLoadingImage from '../../../Assets/distributedCacheLoading.svg'
import noValidMaterialImage from '../../../Assets/noValidMaterials/no-delivery-placeholder.svg'
import {getDefaultMaterialOptions, isButtonDisabled} from '../../../Organisms/OrderIntake/utils'

interface CollectMainContentProps {
  // site selection
  methods: UseFormReturn<BulkOrderFormValues>
  siteOptions: OrderIntakeOption[]
  collectOptions: OrderIntakeOptions
  defaultCollection: OrderRequest | InvalidOrderIntakeOptions
  formChangeHandlers: ReturnType<typeof useBulkOrderFormChangeHandlerWithMethods>
  customerId?: string
  applyMaterialEnteredDescription: boolean
  applyContractItemDescription: boolean

  // persisted materials
  isMaterialPersistency: boolean
  persistedOrderRequests: PersistedOrderRequest[] | undefined
  persistedMaterialsBanner: {
    showBanner: boolean
    existsValidPersistedMaterials: boolean
  }
  onClosePersistedMaterialsBanner: (action: PersistedMaterialsAction) => void

  // materials list
  selectedSite: OrderIntakeOption
  orderFields: FieldArrayWithId<BulkOrderFormValues, 'orders'>[]
  materialEditorController: ReturnType<typeof useMaterialEditorController>
  ordersFormValidity: OrdersFormValidityState[]
  isAllOrdersValid: boolean
  isOrderRequestsCacheLoading: boolean
  noValidMaterial: boolean
  defaultBusinessLine: BusinessLineType
  initialOrderCustomerReference: string
  showHaulierInformation: boolean

  // slot management
  canShowWindowAlert: boolean
  remaining: number

  // callbacks
  addCollection: () => void
  removeCollection: (index: number) => void
  handleEditorActivation: (id: string, item: ActiveMaterialDeliveryItem, index: number) => void
  setIsEditOrderButtonClicked: (val: boolean) => void

  // Layout
  isMobile: boolean
  t: (key: string) => string
  classes: any
  orders: OrderRequest[]
}

export const CollectMainContent = (props: CollectMainContentProps) => {
  const {
    methods,
    siteOptions,
    collectOptions,
    defaultCollection,
    formChangeHandlers,
    orderFields,
    isMobile,
    classes,
    t,
    customerId,
    applyMaterialEnteredDescription,
    applyContractItemDescription,
    isMaterialPersistency,
    persistedOrderRequests,
    persistedMaterialsBanner,
    onClosePersistedMaterialsBanner,
    noValidMaterial,
    isOrderRequestsCacheLoading,
    canShowWindowAlert,
    remaining,
    showHaulierInformation,
    addCollection,
    removeCollection,
    handleEditorActivation,
    setIsEditOrderButtonClicked,
    isAllOrdersValid,
    selectedSite,
    defaultBusinessLine,
    materialEditorController,
    ordersFormValidity,
    initialOrderCustomerReference,
    orders
  } = props
 
  const isDisabled = isButtonDisabled(
    isOrderRequestsCacheLoading,
    initialOrderCustomerReference,
    orders
  )
  return (
    <Grid item xs={12} md={8} style={{paddingRight: 0}}>
      <Paper
        style={isMobile ? {borderRadius: 0, background: '#F5F8FB', padding: 16} : {}}
        className={classes.paperStyle}
      >
        <MaterialSectionHeader data-test-id="collection-site-selection-section-header">
          {t('orderIntake.collectionAddress')}
        </MaterialSectionHeader>
        <Grid item xs={12} md={8}>
          <Controller
            control={methods.control}
            name="selectedSite"
            render={({field}) => (
              <SiteDropdown
                onChange={partialRight(
                  formChangeHandlers.onSiteChange,
                  collectOptions,
                  defaultCollection
                )}
                shippingType={ShippingType.COLLECT}
                selectedSite={field.value}
                options={siteOptions}
                fetchOrderIntakeOptions={() =>
                  invalidateHubQuery('orderRequestsOptions', {
                    customerId,
                    applyMaterialEnteredDescription,
                    applyContractItemDescription
                  })
                }
              />
            )}
          />
        </Grid>

        {isMaterialPersistency &&
          persistedOrderRequests &&
          persistedMaterialsBanner.showBanner &&
          !noValidMaterial && (
            <PersistedMaterialsBanner
              onClose={onClosePersistedMaterialsBanner}
              existsValidMaterials={persistedMaterialsBanner.existsValidPersistedMaterials}
            />
          )}

        {!noValidMaterial ? (
          isOrderRequestsCacheLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" marginTop={10}>
              <img
                src={distributedCacheLoadingImage}
                data-test-id="order-intake-loading-data-from-cache"
                style={{textAlign: 'center', maxWidth: 350}}
                alt={t('orderIntake.loadingDataFromCache')}
              />
              <Box marginTop={2}>
                <Typography
                  variant="body2"
                  color="secondary"
                  style={{textAlign: 'center', maxWidth: 350}}
                >
                  {t('orderIntake.loadingDataFromCache')}
                </Typography>
              </Box>
            </Box>
          ) : (
            <>
              <Stack marginTop={4}>
                {canShowWindowAlert && <SlotOrderWindowAlert remaining={remaining} />}
                <MaterialSectionHeader
                  style={{marginTop: 20}}
                  data-test-id="collection-material-selection-section-header"
                >
                  {t('orderIntake.cementTypeDelivery')}
                </MaterialSectionHeader>
                <TableContainer style={{marginBottom: 20}}>
                  <Table>
                    <Hidden smDown>
                      <MaterialsHeader
                        showHaulierInformation={showHaulierInformation}
                        shippingType={ShippingType.COLLECT}
                        isAllOrdersValid={isAllOrdersValid}
                      />
                    </Hidden>
                    <TableBody>
                      {orderFields.map((collection, index) => {
                        const defaultMaterialOption = getDefaultMaterialOptions(
                          selectedSite.materials,
                          collection.payload
                        )
                        const {status, isSuccess} = materialEditorController.getMaterialFormProps(
                          collection.id
                        )

                        if (!defaultMaterialOption) return null
                        return (
                          <CollectMaterialForm
                            selectedSite={selectedSite}
                            key={collection.id}
                            defaultMaterialOption={defaultMaterialOption}
                            orderIndex={index}
                            removeCollection={() => removeCollection(index)}
                            isSuccess={isSuccess}
                            status={status}
                            shippingType={ShippingType.COLLECT}
                            onActivate={(activeItem) => {
                              setIsEditOrderButtonClicked(true)
                              handleEditorActivation(collection.id, activeItem, index)
                            }}
                            OrdersFormValidityState={ordersFormValidity}
                            isAllOrdersValid={isAllOrdersValid}
                          />
                        )
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                <AddAnotherButton
                  businessLine={defaultBusinessLine}
                  shippingType={ShippingType.COLLECT}
                  disabled={isDisabled}
                  onClick={addCollection}
                />
              </Stack>
              <Box marginTop={2}>
                <ContactSection shippingType={ShippingType.COLLECT} dataTestPrefix="collection" />
              </Box>
            </>
          )
        ) : null}
        {noValidMaterial && (
          <Box display="flex" flexDirection="column" alignItems="center" marginTop={10}>
            <img
              src={noValidMaterialImage}
              data-test-id="order-intake-no-valid-ship-to-image"
              style={{textAlign: 'center', maxWidth: 350}}
              alt={t('orderIntake.invalidMaterials.deliveryNoPossibleForDestination')}
            />
            <Box marginTop={2}>
              <Typography
                variant="body2"
                color="secondary"
                style={{textAlign: 'center', maxWidth: 350}}
              >
                {t('orderIntake.invalidMaterials.deliveryNoPossibleForDestinationMessage')}
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>
    </Grid>
  )
}
